// Web side of the native outbox bridge (spec §6,
// docs/specs/2026-10-29-offline-hotwire-service-worker-design.md).
//
// Calls (web → app), each answered by the app:
//   getOutbox()                                     → { items, paused }
//   enqueueOutbox({ kind, sourceURL, bodyText, title })
//   enqueueOutbox({ clientId })  (retry a rejected item)
//                                                   → { ok, clientId, error, items }
//   deleteOutboxItem({ clientId })                  → { items }
//
// iOS registers WKScriptMessageHandlerWithReply handlers under those names,
// so postMessage returns a promise. Android exposes one @JavascriptInterface
// object, window.DropKindAndroidOutbox, with the same method names; each takes
// the payload as a JSON string and returns the reply as a JSON string.
// Without either (the web, iOS 2.1.0 and older) `supported` is false and
// every caller keeps today's behaviour.
//
// Event (app → web): window.dropkindOutbox.changed({ online, sent }), sent
// once when the connection returns and once after a pass that sent or
// rejected items (a rejection only: `sent` is 0).
// It is re-dispatched on document as OUTBOX_CHANGED_EVENT.
export const OUTBOX_CHANGED_EVENT = "dropkind:outbox-changed"

const CALLS = [ "getOutbox", "enqueueOutbox", "deleteOutboxItem" ]

function iosHandlers() {
  const handlers = window.webkit?.messageHandlers
  return handlers && CALLS.every((name) => handlers[name]) ? handlers : null
}

function androidInterface() {
  const bridge = window.DropKindAndroidOutbox
  return bridge && CALLS.every((name) => typeof bridge[name] === "function") ? bridge : null
}

async function call(name, payload = {}) {
  const ios = iosHandlers()
  if (ios) return ios[name].postMessage(payload)

  const android = androidInterface()
  if (android) {
    const reply = android[name](JSON.stringify(payload))
    return typeof reply === "string" ? JSON.parse(reply) : reply
  }

  throw new Error(`Native outbox is not available (${name})`)
}

export const NativeOutbox = {
  get supported() {
    return Boolean(iosHandlers() || androidInterface())
  },

  async getOutbox() {
    if (!this.supported) return { items: [], paused: null }

    const reply = await call("getOutbox")
    return { items: reply?.items || [], paused: reply?.paused || null }
  },

  // Answers only after the app has written the item: clear the form on `ok`.
  enqueue({ kind, sourceURL = null, bodyText = null, title = null }) {
    return call("enqueueOutbox", { kind, sourceURL, bodyText, title })
  },

  retry(clientId) {
    return call("enqueueOutbox", { clientId })
  },

  delete(clientId) {
    return call("deleteOutboxItem", { clientId })
  }
}

window.dropkindOutbox = {
  changed(detail = {}) {
    document.dispatchEvent(new CustomEvent(OUTBOX_CHANGED_EVENT, { detail }))
  }
}
